import { useWizard } from "@shared/hooks/useWizard/useWizard";
import { useCombinedElementHeight } from "@shared/hooks/useCombinedElementHeight";
import React, { createContext, useContext } from "react";

/**
 * A single instance of a wizard context
 */
type WizardContextInstance = ReturnType<typeof useWizard>;

/**
 * A recursive type representing a wizard context at any depth (0 to N).
 * This can have a `parent`, representing the previous context in the chain,
 * or `null` if it's the root of the tree.
 */
type WizardContext = WizardContextInstance & {
  parent: WizardContext | null;
};

const WizardContext = createContext<WizardContext | null>(null);

/**
 * Allows a wizard created with `useWizard` to take advantage of dependency injection
 */
export function WizardProvider({
  wizard,
  children,
  fallback = null,
  error = null,
  fallbackOffsetIds,
}: {
  /**
   * The wizard that we will be providing to the children subtree
   */
  wizard: WizardContextInstance;
  children: React.ReactNode;
  /**
   * A component displayed as a loading fallback until the wizard is deemed ready, however it chooses to do so.
   *
   * This hosts a similar API to Suspense API, but does not actually use suspense internally.
   */
  fallback?: React.ReactNode;
  /**
   * A component displayed when the wizard encounters a catastrophic error the user cannot recover from.
   */
  error?: React.ReactNode;
  /**
   * DOM element IDs whose heights offset the fallback from the top.
   * When provided, the fallback overlay is scoped to the wizard container
   * (position: relative) and shifted down by the combined height of these
   * elements — keeping navbars, progress bars, etc. visible.
   */
  fallbackOffsetIds?: string[];
}) {
  const parentContext = useContext(WizardContext);
  const topOffset = useCombinedElementHeight(fallbackOffsetIds);

  return (
    <WizardContext.Provider
      value={{
        ...wizard,
        parent: parentContext,
      }}
    >
      <div
        id="wizard-fallback"
        data-testid="wizard-fallback"
        style={{
          display: wizard.isReady ? "none" : "flex",
          position: "absolute",
          top: topOffset,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: Number.MAX_SAFE_INTEGER,
        }}
      >
        {fallback}
      </div>
      {error && wizard.error ? error : children}
    </WizardContext.Provider>
  );
}

/**
 * Retrieves the wizard context at the current depth or
 * traverses up the chain to find a named wizard
 */
export function useWizardContext(
  targetName?: string
): WizardContext | Record<string, never> {
  const context = useContext(WizardContext);

  if (!context) {
    throw new Error(
      `useWizardContext must be used within a WizardProvider. For locally scoped wizards,
       use useWizard directly instead.`
    );
  }

  if (!targetName || context.name === targetName) {
    return context;
  }

  // Traverse up the context chain to find the named Wizard
  let currentContext = context.parent;
  while (currentContext) {
    if (currentContext.name === targetName) {
      return currentContext;
    }
    currentContext = currentContext.parent;
  }

  throw new Error(`WizardProvider with name "${targetName}" not found`);
}
